import { inject, Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import {
  Actions, createEffect, ofType
} from '@ngrx/effects';
import {
  Observable, EMPTY, catchError, exhaustMap, finalize, from, map, of, switchMap, tap, timer
} from 'rxjs';
import dayjs from 'dayjs';
import {
  adminLoginFailure,
  adminLoginRedirectStarted,
  adminLoginRequest,
  adminLoginSuccess,
  adminLogout,
  adminSessionExpired,
  adminTokenRefreshFailed,
  adminTokenRefreshRequested,
  adminTokenRefreshSucceeded,
  initAdminAuth,
  emailValidationFailure,
  emailValidationRequest,
  emailValidationSuccess,
  restoreAdminSession
} from './auth.actions';
import { credentialsLoginMfaFallbackRequested } from '../../../features/login/store/login.actions';
import { AuthService } from '../service/auth.service';
import { clearMsalRuntimeConfig, loginRequest } from '../config/msal';
import { LoadScreenService } from '../../../shared/provider/load-state-provider/load-screen.service';
import { MessageService } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import { UserProfile } from '../type/auth.type';
import { isBaseLogoutRequest } from '../../guards/base-logout.redirect';

@Injectable()
export class AdminAuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly authService = inject(AuthService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly msalService = inject(MsalService);
  private readonly loadScreenService = inject(LoadScreenService);
  private messageService = inject(MessageService);

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initAdminAuth),
      exhaustMap(() => this.resolveSession$())
    )
  );

  sessionExpiration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        adminLoginSuccess,
        restoreAdminSession,
        adminTokenRefreshSucceeded,
        adminLogout,
        adminSessionExpired
      ),
      switchMap((action) => {
        if (action.type === adminLogout.type || action.type === adminSessionExpired.type) {
          return EMPTY;
        }

        const expiresAt = dayjs(action.expiresAt);
        const millisecondsUntilExpiration = expiresAt.diff(dayjs());

        if (!expiresAt.isValid() || millisecondsUntilExpiration <= 0) {
          return of(adminTokenRefreshRequested());
        }

        const refreshDelay = Math.max(millisecondsUntilExpiration - 60_000, 0);

        return timer(refreshDelay).pipe(
          map(() => adminTokenRefreshRequested())
        );
      })
    )
  );

  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminTokenRefreshRequested),
      exhaustMap(() => this.authService.refreshAuthToken().pipe(
        map((session) => adminTokenRefreshSucceeded(session)),
        catchError((err: unknown) => {
          const error = this.resolveErrorMessage(err, 'Session refresh failed');
          return of(adminTokenRefreshFailed({ error }));
        })
      ))
    )
  );

  expireAfterRefreshFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminTokenRefreshFailed),
      map(() => adminSessionExpired())
    )
  );

  private resolveSession$():
  Observable<ReturnType<typeof restoreAdminSession> | ReturnType<typeof adminSessionExpired>> {
    const accounts = this.msalService.instance.getAllAccounts();
    const account = this.msalService.instance.getActiveAccount() ?? accounts[0];
    const cookieToken = this.authService.getStoredAuthToken()?.trim();

    if (cookieToken === undefined || cookieToken.length === 0) {
      return this.restoreWithoutStoredSession$(account);
    }

    try {
      const storedSession = this.authService.validateAuthSession(cookieToken, true);

      if (dayjs().isBefore(storedSession.expiresAt)) {
        const persistedSession = this.authService.setAuthToken(storedSession.token);

        return of(restoreAdminSession({ ...persistedSession }));
      }
    } catch {
      this.authService.deleteCookies();
      return this.restoreWithoutStoredSession$(account);
    }

    return this.refreshStoredSession$(account);
  }

  private restoreWithoutStoredSession$(
    account: AccountInfo | undefined
  ): Observable<ReturnType<typeof restoreAdminSession> | ReturnType<typeof adminSessionExpired>> {
    return account !== undefined
      ? this.restoreMicrosoftSession$(account)
      : of(adminSessionExpired());
  }

  private refreshStoredSession$(
    microsoftAccount: AccountInfo | undefined
  ): Observable<ReturnType<typeof restoreAdminSession> | ReturnType<typeof adminSessionExpired>> {
    return this.authService.refreshAuthToken().pipe(
      map((session) => restoreAdminSession(session)),
      catchError(() => microsoftAccount !== undefined
        ? this.restoreMicrosoftSession$(microsoftAccount)
        : of(adminSessionExpired()))
    );
  }

  private acquireMicrosoftToken$(account: AccountInfo): Observable<string> {
    this.msalService.instance.setActiveAccount(account);
    return from(
      this.msalService.instance.acquireTokenSilent({
        account,
        scopes: loginRequest.scopes,
      })
    ).pipe(map((result) => result.accessToken));
  }

  private exchangeAndPersistToken$(
    accessToken: string
  ): Observable<ReturnType<typeof restoreAdminSession>> {
    return this.authService.validateMicrosoftToken(accessToken).pipe(
      map(({ token, userProfile }) =>
        restoreAdminSession(this.authService.setAuthToken(token, userProfile)))
    );
  }

  private restoreMicrosoftSession$(
    account: AccountInfo
  ): Observable<ReturnType<typeof restoreAdminSession> | ReturnType<typeof adminSessionExpired>> {
    this.loadScreenService.openFullScreen('Signing you in...', true);

    return this.acquireMicrosoftToken$(account).pipe(
      switchMap((accessToken) => this.exchangeAndPersistToken$(accessToken)),
      catchError((err: unknown) => {
        let error = 'User validation failed';

        if (err instanceof HttpErrorResponse) {
          error = err.error?.description ?? error;
        } else if (err instanceof Error) {
          error = err.message;
        }

        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error,
        });

        this.loadScreenService.closeBlocking();
        return of(adminSessionExpired());
      })
    );
  }

  emailValidation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(emailValidationRequest),
      exhaustMap(({ email }) => {
        const normalizedEmail = email.trim();

        if (normalizedEmail.length === 0) {
          return of(emailValidationFailure({ error: 'Email is required' }));
        }

        this.loadScreenService.open('Verifying, please wait');

        return this.authService.emailValidation(normalizedEmail).pipe(
          map((response) => emailValidationSuccess({
            response,
            email: normalizedEmail,
          })),
          catchError((err: HttpErrorResponse) => {
            const error = err.error?.description ?? 'Email validation failed';

            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error,
            });

            return of(emailValidationFailure({ error }));
          }),
          finalize(() => this.loadScreenService.close())
        );
      })
    )
  );

  loginAfterEmailValidation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(emailValidationSuccess),
      exhaustMap(({ response, email }) => {
        const organizationName = response.organization?.name?.trim();
        const normalizedEmail = email.trim();
        const profileEmail = response.userProfile?.emailAddress?.trim();
        const loginHint = normalizedEmail.length > 0 ? normalizedEmail : profileEmail;
        const identityProvider = response
          .organizationIdentityProviders?.find((idp) => idp.isActive);
        const clientId = identityProvider?.clientId?.trim();
        const tenantId = identityProvider?.tenantId?.trim();

        if (organizationName === undefined || identityProvider === undefined) {
          return EMPTY;
        }

        if (identityProvider.identityProvider.name.toLowerCase() === 'username & password') {
          return EMPTY;
        }

        return this.authService.adminLoginWithMicrosoft({
          loginHint,
          clientId,
          tenantId,
        }).pipe(
          map(() => adminLoginRedirectStarted()),
          catchError((err: unknown) => {
            const error = err instanceof Error ? err.message : 'Login failed';
            return of(adminLoginFailure({ error }));
          })
        );
      })
    )
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(adminLoginRequest),
      exhaustMap((action) => this.handleAdminLoginRequest(action))
    )
  );

  redirectCredentialsToCallback$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(adminLoginSuccess),
        tap(() => {
          if (this.router.url !== '/auth/callback') {
            void this.router.navigate(['/auth/callback']);
          }
        })
      ),
    { dispatch: false }
  );

  redirectRestoredSessionToCallback$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(restoreAdminSession),
        tap(() => {
          const isBaseLogoutReturn = isBaseLogoutRequest(this.location.path());

          if (this.router.url === '/login'
            || (this.router.url === '/' && !isBaseLogoutReturn)) {
            void this.router.navigate(['/auth/callback']);
          }
        })
      ),
    { dispatch: false }
  );

  private handleAdminLoginRequest(
    {
      method,
      email,
      password,
    }: ReturnType<typeof adminLoginRequest>) {
    if (method === 'credentials') {
      if (email === undefined || email === null || password === undefined || password === null || email.trim() === '' || password.trim() === '') {
        return of(adminLoginFailure({ error: 'Email and password are required' }));
      }

      this.loadScreenService.open('Authenticating, please wait');
      let mfaLoaderHandoffStarted = false;

      return this.authService.adminLoginWithCredentials({
        email: email as string,
        password: password as string,
      }).pipe(
        map((response) => {
          return this.processSuccessfulLogin(response);
        }),
        catchError((err: unknown) => {
          const errorCode = err instanceof HttpErrorResponse
            ? err.error?.code ?? 'UNKNOWN_ERROR'
            : 'UNKNOWN_ERROR';
          const errorDescription = this.resolveErrorMessage(err, 'Login failed');

          if (errorCode === 'Device.NotFound') {
            const passKey = this.extractPassKeyOtp(errorDescription);
            mfaLoaderHandoffStarted = true;

            return of(
              credentialsLoginMfaFallbackRequested({
                email: email.trim(),
                passKey,
              })
            );
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorDescription,
          });

          return of(adminLoginFailure({ error: errorDescription }));
        }),
        finalize(() => {
          if (!mfaLoaderHandoffStarted) {
            this.loadScreenService.close();
          }
        })
      );
    }

    return this.authService.adminLoginWithMicrosoft()
      .pipe(
        map(() => adminLoginRedirectStarted()),
        catchError((err: unknown) => {
          const error =
            err instanceof Error ? err.message : 'Login failed';
          return of(adminLoginFailure({ error }));
        })
      );
  }

  private processSuccessfulLogin({ token, userProfile }: {
    token: string;
    userProfile?: UserProfile;
  }) {
    const session = this.authService.setAuthToken(token, userProfile);

    return adminLoginSuccess({ ...session });
  }

  private extractPassKeyOtp(description: unknown): string {
    if (typeof description !== 'string') {
      return '';
    }

    const match = description.match(/PassKeyOtp:\s*([^\s]+)/i);
    return match?.[1]?.trim() ?? '';
  }

  private resolveErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      return error.error?.description ?? fallback;
    }

    return error instanceof Error ? error.message : fallback;
  }

  /** Clear sessionStorage and navigate to the public login page */
  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(adminLogout),
        tap(() => {
          const hasMicrosoftSession = this.msalService.instance.getAllAccounts().length > 0;

          this.clearBrowserSessionData();

          if (hasMicrosoftSession) {
            this.authService.adminLogoutWithMicrosoft();
            return;
          }

          void this.router.navigate(['/login']);
        })
      ),
    { dispatch: false }
  );

  /** Expired session — same teardown as logout */
  sessionExpired$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(adminSessionExpired),
        tap(() => {
          this.clearBrowserSessionData();
          void this.router.navigate(['/login']);
        })
      ),
    { dispatch: false }
  );

  private clearBrowserSessionData(): void {
    this.authService.deleteCookies();
    sessionStorage.clear();
    localStorage.clear();

    clearMsalRuntimeConfig();

    const instance = this.msalService.instance as {
      setActiveAccount?: (account: AccountInfo | null) => void;
      clearCache?: () => Promise<void>;
    };

    if (typeof instance.setActiveAccount === 'function') {
      instance.setActiveAccount(null);
    }

    if (typeof instance.clearCache === 'function') {
      void instance.clearCache();
    }
  }
}
